import { useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, InputNumber, notification, Select, Switch } from "antd";
import { ExclamationCircleFilled, InfoCircleFilled } from "@ant-design/icons";

import { ClientStatus, IClient } from "@/modules/client/clients.interface";
import { ClientHttpService } from "@/modules/client/services/client-http.service";
import { ReduxClientService } from "@/modules/client/services/redux-client.service";

import { ClientAbilityContext } from "@/shared/contexts/ability.context";
import { Action, Subject } from "@/shared/services/permissions/casl-ability.factory";
import { filterByAbility } from "@/shared/services/permissions/filterByAbility.utils";
import { addClients, selectClientData } from "@/shared/services/store/clientStore";

import { SubmitButton } from "@/ui-components/commonComponents/Button/SubmitButton";
import { CustomInput } from "@/ui-components/commonComponents/Input/Input";
import { FormInputWrapper } from "@/ui-components/commonComponents/Input/FormInputWrapper";
import { UploadPhoto, UploadFolder } from "@/ui-components/commonComponents/UploadPhoto/UploadPhoto";

import { ReactComponent as SaveSVG } from "@/assets/icons/feather-icons/save.svg";
import cn from "classnames";

import antTooltipOverlay from "@/shared/styles/antTooltipOverlay.module.css";
import style from "./style.module.css";
import { startCase } from "lodash";

const formItemLayout = {
  labelCol: {
    span: 24,
  },
  wrapperCol: {
    span: 24,
  },
};

const CANDIDATE_RETENTION_PERIOD_LABELS = {
  INPUT_ENABLED: {
    PRIMARY:
      "Candidate identifying information will be permanently deleted after the specified number of days. Assessment results will remain available in a de-identified form. Allowed range: 90 to 2,190 days (6 years).",
    SECONDARY:
      'Changes to this value are immediately applied when clicking "Save". If the retention period is shortened, any candidate records that now fall into the shorter retention period will be processed, and identifiable data removed. This data will not be recoverable.',
  },
  INPUT_DISABLED: {
    PRIMARY:
      "Candidate identifying information will be removed after the specified number of days. Reach out to your Client Success Team if you have questions about the Data Retention Period.",
  },
};

export default function GeneralClientSettings({ client }: { client: IClient }) {
  const dispatch = useDispatch();
  const reduxClients = useSelector(selectClientData);

  const [form] = Form.useForm();
  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }

    return e && e.fileList;
  };
  const [imageUrl, setImageUrl] = useState<string>(client.logoUrl ?? "");
  const [isLoading, setIsLoading] = useState(false);

  const handleFinish = async (values: {
    name: string;
    status?: ClientStatus;
    candidateRetentionPeriodDays: number;
    insightsEnabled?: boolean;
  }) => {
    if (!client.clientId) {
      throw new Error("There is no client ID provided");
    }

    const clientData = filterByAbility(ability, Action.Update, Subject.CLIENT, {
      name: values.name,
      logoUrl: imageUrl,
      status: values.status,
      candidateRetentionPeriodDays: values.candidateRetentionPeriodDays,
      insightsEnabled: values.insightsEnabled,
    });

    try {
      setIsLoading(true);
      await ClientHttpService.updateClientById(clientData, Number(client.clientId));

      dispatch(
        addClients(
          ReduxClientService.updateClientService(reduxClients, {
            ...client,
            ...clientData,
          }),
        ),
      );
      notification.success({
        message: "Client updated successfully.",
      });
    } catch (e) {
      notification.error({
        message: "There was an error updating client settings. Please try again or contact support.",
      });
      console.error(`clientDataUPD: ${e}`);
    } finally {
      setIsLoading(false);
    }
  };

  const ability = useContext(ClientAbilityContext);
  const canUpdateClientLogo = ability.can(Action.Update, Subject.CLIENT, "logoUrl");
  const canUpdateClientName = ability.can(Action.Update, Subject.CLIENT, "name");
  const canUpdateClientStatus = ability.can(Action.Update, Subject.CLIENT, "status");
  const canReadClientStatus = ability.can(Action.Read, Subject.CLIENT, "status");
  const canUpdateClientInsights = ability.can(Action.Update, Subject.CLIENT, "insightsEnabled");
  const canReadClientInsights = ability.can(Action.Read, Subject.CLIENT, "insightsEnabled");
  const canUpdateCandidateRetentionPeriod = ability.can(Action.Update, Subject.CLIENT, "candidateRetentionPeriodDays");
  let candidateDataRetentionPeriodAriaLabel = undefined;

  if (canUpdateCandidateRetentionPeriod) {
    candidateDataRetentionPeriodAriaLabel = `${CANDIDATE_RETENTION_PERIOD_LABELS.INPUT_ENABLED.PRIMARY}. Caution. ${CANDIDATE_RETENTION_PERIOD_LABELS.INPUT_ENABLED.SECONDARY}`;
  } else {
    candidateDataRetentionPeriodAriaLabel = CANDIDATE_RETENTION_PERIOD_LABELS.INPUT_DISABLED.PRIMARY;
  }

  return (
    <div className={style.container}>
      <Form
        form={form}
        {...formItemLayout}
        onFinish={handleFinish}
        initialValues={{
          name: client.name,
          logo: imageUrl,
          status: client.status,
          candidateRetentionPeriodDays: client.candidateRetentionPeriodDays,
          insightsEnabled: client.insightsEnabled,
        }}
        requiredMark={false}
      >
        <div className={style.form}>
          <Form.Item
            name="logo"
            label="Client Logo"
            getValueFromEvent={normFile}
            className={cn(style.photoContainer, style.inputTitle)}
          >
            <UploadPhoto
              disabled={!canUpdateClientLogo}
              clientID={String(client.clientId)}
              onUpload={setImageUrl}
              uploadText="Client Logo"
              defaultImageUrl={imageUrl}
              uploadFolder={UploadFolder.clientProfileFolder}
            />
          </Form.Item>
          <div className={style.innerWrapper}>
            <Form.Item
              className={style.inputTitle}
              key="name"
              name="name"
              label="Client Name"
              rules={[
                {
                  required: canUpdateClientName,
                  message: "Client name is required",
                },
              ]}
            >
              <CustomInput placeholder="Type..." disabled={!canUpdateClientName} />
            </Form.Item>
            {canReadClientStatus && (
              <Form.Item
                className={style.inputTitle}
                name="status"
                label="Status"
                rules={[
                  {
                    required: canUpdateClientStatus,
                    message: "Client status is required",
                  },
                ]}
              >
                <Select
                  options={[
                    { value: ClientStatus.Active, label: startCase(ClientStatus.Active) },
                    { value: ClientStatus.Inactive, label: startCase(ClientStatus.Inactive) },
                  ]}
                  disabled={!canUpdateClientStatus}
                />
              </Form.Item>
            )}
            {canReadClientInsights && (
              <fieldset className={style.insightsFieldset}>
                <legend>Assessment Insights</legend>
                <div className={style.switchField}>
                  <Form.Item name="insightsEnabled" valuePropName="checked" noStyle>
                    <Switch id="insightsEnabled" disabled={!canUpdateClientInsights} />
                  </Form.Item>
                  <label htmlFor="insightsEnabled">Show Assessment Insights</label>
                </div>
              </fieldset>
            )}
            <div className={style.retentionSection}>
              <Form.Item
                name="candidateRetentionPeriodDays"
                rules={[
                  { required: canUpdateCandidateRetentionPeriod, message: "Retention period is required" },
                  { type: "number", min: 90, message: "Must be at least 90 days" },
                  { type: "number", max: 2190, message: "Cannot exceed 2190 days (6 years)" },
                ]}
                label="Candidate Data Retention Period"
                className={style.inputTitle}
                tooltip={{
                  overlayClassName: antTooltipOverlay.tooltipWrapper,
                  title: (
                    <div>
                      {canUpdateCandidateRetentionPeriod ? (
                        <>
                          <p>{CANDIDATE_RETENTION_PERIOD_LABELS.INPUT_ENABLED.PRIMARY}</p>
                          <div className={style.tooltipCaution}>
                            <div className={style.tooltipCautionHeading}>
                              <ExclamationCircleFilled
                                width={20}
                                height={20}
                                className={style.tooltipCautionIcon}
                                aria-hidden
                              />
                              <strong>Caution</strong>
                            </div>
                            <p>{CANDIDATE_RETENTION_PERIOD_LABELS.INPUT_ENABLED.SECONDARY}</p>
                          </div>
                        </>
                      ) : (
                        <p>{CANDIDATE_RETENTION_PERIOD_LABELS.INPUT_DISABLED.PRIMARY}</p>
                      )}
                    </div>
                  ),
                  trigger: ["hover", "focus"],
                  icon: (
                    <InfoCircleFilled
                      aria-label={candidateDataRetentionPeriodAriaLabel}
                      tabIndex={canUpdateCandidateRetentionPeriod ? -1 : 0} // if user doesn't have the ability to edit this input, they should still be able to navigate to the tooltip to be presented instructons.
                    />
                  ),
                }}
              >
                <FormInputWrapper>
                  {(props) => (
                    <div className={style.daysInputContainer}>
                      <p aria-hidden="true">Candidate identifiable data will be retained for</p>
                      <InputNumber
                        min={90}
                        max={2190}
                        precision={0}
                        disabled={!canUpdateCandidateRetentionPeriod}
                        {...props}
                      />
                      <p aria-hidden="true">days</p>
                    </div>
                  )}
                </FormInputWrapper>
              </Form.Item>
            </div>
          </div>
        </div>

        <SubmitButton
          icon={<SaveSVG aria-hidden="true" />}
          isLoading={isLoading}
          disabled={
            !(
              canUpdateClientLogo ||
              canUpdateClientName ||
              canUpdateClientStatus ||
              canUpdateClientInsights ||
              canUpdateCandidateRetentionPeriod
            )
          }
        >
          Save
        </SubmitButton>
      </Form>
    </div>
  );
}
